import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AddMenuWrap,
  AddFab,
  AddPopover,
  AddSearch,
  AddList,
  AddItem,
  AddItemIconStep,
  AddItemIconSoon,
  AddItemInfo,
  AddItemName,
  AddItemDesc,
  AddItemSoon,
  AddSoonTag,
  AddDivider,
  AddEmpty,
} from './styles'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconMap: Record<string, any> = { step: AddItemIconStep }

export default function AddNodeMenu({ onAdd }: { onAdd: (type: string) => void }) {
  const { t } = useTranslation()
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')
  const menuRef  = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const NODE_TYPES = [
    {
      type: 'step',
      icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
      label: t('add_menu.step_label'),
      desc:  t('add_menu.step_desc'),
    },
  ]

  const COMING_SOON = [
    { label: t('add_menu.condition_label'), desc: t('add_menu.condition_desc') },
    { label: t('add_menu.delay_label'),     desc: t('add_menu.delay_desc')     },
    { label: t('add_menu.human_label'),     desc: t('add_menu.human_desc')     },
  ]

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const filtered = NODE_TYPES.filter(
    (n) =>
      n.label.toLowerCase().includes(search.toLowerCase()) ||
      n.desc.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <AddMenuWrap ref={menuRef}>
      <AddFab $open={open} onClick={() => setOpen(!open)} title={t('add_menu.add_node_title')}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </AddFab>

      {open && (
        <AddPopover>
          <AddSearch>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              placeholder={t('add_menu.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </AddSearch>

          <AddList>
            {filtered.map((n) => {
              const IconWrap = IconMap[n.type] || AddItemIconSoon
              return (
                <AddItem
                  key={n.type}
                  onClick={() => { onAdd(n.type); setOpen(false); setSearch('') }}
                >
                  <IconWrap>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={n.icon} />
                    </svg>
                  </IconWrap>
                  <AddItemInfo>
                    <AddItemName>{n.label}</AddItemName>
                    <AddItemDesc>{n.desc}</AddItemDesc>
                  </AddItemInfo>
                </AddItem>
              )
            })}
            {filtered.length === 0 && <AddEmpty>{t('add_menu.no_results')}</AddEmpty>}
          </AddList>

          {!search && (
            <>
              <AddDivider />
              <AddList>
                {COMING_SOON.map((n) => (
                  <AddItemSoon key={n.label}>
                    <AddItemIconSoon>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </AddItemIconSoon>
                    <AddItemInfo>
                      <AddItemName>{n.label}</AddItemName>
                      <AddItemDesc>{n.desc}</AddItemDesc>
                    </AddItemInfo>
                    <AddSoonTag>{t('common.coming_soon')}</AddSoonTag>
                  </AddItemSoon>
                ))}
              </AddList>
            </>
          )}
        </AddPopover>
      )}
    </AddMenuWrap>
  )
}
